import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Slider,
  Stack,
  Select,
  TextField,
  Typography,
  Pagination,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseRounded,
  CloudUploadRounded,
  DeleteOutlineRounded,
  EditRounded,
  MusicNoteRounded,
  PlaylistAddRounded,
  PlayArrowRounded,
  PauseRounded,
  QueueMusicRounded,
  SearchRounded,
  VisibilityRounded,
  SkipNextRounded,
  SkipPreviousRounded,
  VolumeDownRounded,
  VolumeMuteRounded,
  VolumeUpRounded,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import ViewToggle, { useViewMode } from '../components/ui/ViewToggle';
import PageHeader from '../components/ui/PageHeader';
import FormDialogShell, { gradientButtonSx } from '../components/ui/FormDialogShell';
import TabFilterBar from '../components/ui/TabFilterBar';
import NoteStyleCard, { GlassIconTile } from '../components/ui/NoteStyleCard';
import {
  addTuneToPlaylist,
  createPlaylist,
  deleteTune,
  getPlaylist,
  listPlaylistsApi,
  listTunes,
  updateTune,
  updatePlaylist,
  deletePlaylist,
  uploadTune,
  PlaylistItem,
  TuneItem,
  UploadQuota,
} from '../services/api';
import { getStoredUser as getAuthUser } from '../utils/auth';
import { useSoundBoost } from '../hooks/useSoundBoost';
import SoundSettingsButton from '../components/ui/SoundSettingsButton';

const API_BASE = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/+$/, '');
const API_ORIGIN = API_BASE.replace(/\/api$/i, '');
const absoluteUrl = (u?: string) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${API_ORIGIN}${u}`;
  return `${API_ORIGIN}/${u}`;
};

const displayUploader = (t?: TuneItem | null) =>
  t?.uploadedBy?.fullName || t?.uploadedBy?.username || t?.uploadedBy?.email || 'Unknown uploader';

const formatBytes = (size?: number) => {
  if (!size || size <= 0) return '—';
  const mb = size / (1024 * 1024);
  if (mb < 0.1) return '<0.1 MB';
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const formatDate = (v?: string) => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
};

const formatPlaybackTime = (secs?: number) => {
  if (!secs || !isFinite(secs) || secs < 0) return '0:00';
  const total = Math.floor(secs);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const BRAND_PURPLE = '#4776E6';
const BRAND_PURPLE_DARK = '#8E54E9';
const BRAND_ORANGE = '#F6A938';
const TUNES_COLORS: [string, string] = ['#4776E6', '#8E54E9'];
const tunesLabelSx = {
  fontWeight: 800, fontSize: 12.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', mb: 0.75,
} as const;
const tunesInputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: 'background.paper', fontWeight: 700 },
  '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: 'rgba(71,118,230,.3)' },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(71,118,230,.6)' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#4776E6' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 5px rgba(71,118,230,.16)' },
} as const;
const tunesCancelSx = {
  borderRadius: '999px', px: 2.5, border: '2px solid', borderColor: 'divider', color: 'text.primary',
} as const;

const getCurrentUser = () => {
  return getAuthUser<Record<string, any>>() || {};
};

const getQuotaLimitReachedMessage = (limit: number, kind: 'tune' | 'video') =>
  `Upload limit reached. You can upload up to ${limit} ${kind}${limit === 1 ? '' : 's'}.`;

const getFileSizeLimitMessage = (sizeLimit: number) =>
  `File too large. Please upload a file below ${sizeLimit} MB.`;

const getTunesModeFromPath = (pathname: string): 'playlist' | 'mine' | 'others' | 'playlists' => {
  const path = pathname.replace(/\/+$/, '');
  if (path.startsWith('/tunes/playlists')) return 'playlists';
  if (path.startsWith('/tunes/others')) return 'others';
  if (path.startsWith('/tunes/own')) return 'mine';
  return 'playlist';
};

export default function Tunes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { playlistId } = useParams<{ playlistId?: string }>();
  const storedUser = getCurrentUser();
  const currentUserId = typeof storedUser?._id === 'string' ? storedUser._id : '';
  const [tunes, setTunes] = useState<TuneItem[]>([]);
  const [tuneQuota, setTuneQuota] = useState<UploadQuota | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingTuneId, setDeletingTuneId] = useState<string | null>(null);
  const tuneBulk = useBulkSelect();
  const playlistBulk = useBulkSelect();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [playlistDlgOpen, setPlaylistDlgOpen] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistError, setPlaylistError] = useState('');
  const [editingPlaylist, setEditingPlaylist] = useState<PlaylistItem | null>(null);
  const [playlists, setPlaylists] = useState<PlaylistItem[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<string>('all');
  const [playlistTunes, setPlaylistTunes] = useState<TuneItem[]>([]);
  const [mode, setMode] = useState<'playlist' | 'mine' | 'others' | 'playlists'>('playlist');
  const [playlistSelectOpen, setPlaylistSelectOpen] = useState(false);
  const [playlistTarget, setPlaylistTarget] = useState<TuneItem | null>(null);
  const [playlistChoice, setPlaylistChoice] = useState<string>('');
  const [playlistChoiceError, setPlaylistChoiceError] = useState('');
  const [tuneEditOpen, setTuneEditOpen] = useState(false);
  const [editingTune, setEditingTune] = useState<TuneItem | null>(null);
  const [editTuneTitle, setEditTuneTitle] = useState('');
  const [editTuneArtist, setEditTuneArtist] = useState('');
  const [editTuneError, setEditTuneError] = useState('');
  const [savingTuneEdit, setSavingTuneEdit] = useState(false);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useViewMode('quillstack.tunes.view', 'grid');
  // Per-field validation messages, shown right under each field.
  const [uploadErrors, setUploadErrors] = useState<{ title?: string; artist?: string; file?: string }>({});
  const [editTuneErrors, setEditTuneErrors] = useState<{ title?: string; artist?: string }>({});
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tuneVolume, setTuneVolume] = useState(1);
  const [tuneMuted, setTuneMuted] = useState(false);
  const volumeBeforeMuteRef = useRef(1);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sound = useSoundBoost(audioRef);
  const intendedPlaybackRef = useRef(false);
  const isSeekingRef = useRef(false);
  const savedTimeRef = useRef<number>(0);
  const isReloadingRef = useRef(false);
  const [current, setCurrent] = useState<TuneItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackStatusMessage, setPlaybackStatusMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const tuneLimitReached = tuneQuota?.limit != null && tuneQuota.count >= tuneQuota.limit;

  const load = async (term = '') => {
    setLoading(true);
    try {
      const data = await listTunes(term);
      setTunes(data.items);
      setTuneQuota(data.quota);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load tunes';
      Swal.fire('Error', msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadQuota = async () => {
    try {
      const data = await listTunes('');
      setTuneQuota(data.quota);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load tune quota';
      Swal.fire('Error', msg, 'error');
    }
  };

  const loadPlaylists = async () => {
    try {
      const pls = await listPlaylistsApi();
      setPlaylists(pls);
      if (!selectedPlaylist && pls.length) {
        setSelectedPlaylist('all');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load playlists';
      Swal.fire('Error', msg, 'error');
    }
  };

  const loadPlaylistTunes = async (id?: string) => {
    setLoading(true);
    const pid = id || selectedPlaylist;
    if (!pid) {
      setPlaylistTunes([]);
      setLoading(false);
      return;
    }
    try {
      if (pid === 'all') {
        const pls = playlists.length ? playlists : await listPlaylistsApi();
        const seen = new Set<string>();
        const collected: TuneItem[] = [];
        for (const pl of pls) {
          try {
            const data = await getPlaylist(pl._id);
            (data?.tunes || []).forEach((t) => {
              if (!seen.has(t._id)) {
                seen.add(t._id);
                collected.push(t);
              }
            });
          } catch {
            // skip failed playlist fetch
          }
        }
        setPlaylistTunes(collected);
      } else {
        const playlist = await getPlaylist(pid);
        setPlaylistTunes(playlist?.tunes || []);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Unable to load playlist';
      Swal.fire('Error', msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectPlaylist = (playlistId: string) => {
    setSelectedPlaylist(playlistId);
  };

  const goToTunes = () => {
    navigate('/tunes');
  };

  const goToOwn = () => {
    navigate('/tunes/own');
  };

  const goToOthers = () => {
    navigate('/tunes/others');
  };

  const goToPlaylists = () => {
    navigate('/tunes/playlists');
  };

  const openPlaylistSongs = (playlistIdToOpen: string) => {
    navigate(`/tunes/playlists/${playlistIdToOpen}`);
  };

  const openCreatePlaylist = () => {
    setEditingPlaylist(null);
    setPlaylistName('');
    setPlaylistError('');
    setPlaylistDlgOpen(true);
  };

  const openEditPlaylist = (playlist: PlaylistItem) => {
    setEditingPlaylist(playlist);
    setPlaylistName(playlist.name || '');
    setPlaylistError('');
    setPlaylistDlgOpen(true);
  };

  const openEditTune = (tune: TuneItem) => {
    setEditingTune(tune);
    setEditTuneTitle(tune.title || tune.originalName || tune.filename || '');
    setEditTuneArtist(tune.artist || '');
    setEditTuneError('');
    setTuneEditOpen(true);
  };

  useEffect(() => {
    setMode(getTunesModeFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [mode, search, selectedPlaylist, playlistId]);

  useEffect(() => {
    if (mode !== 'playlist') return;
    if (!search.trim()) {
      load(search);
      return;
    }
    const t = setTimeout(() => { load(search); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, mode]);

  useEffect(() => {
    loadPlaylists();
    loadQuota();
  }, []);

  useEffect(() => {
    if (mode !== 'playlist' || !selectedPlaylist) return;
    loadPlaylistTunes(selectedPlaylist);
  }, [mode, selectedPlaylist]);

  useEffect(() => {
    if (mode !== 'playlists') return;
    if (playlistId) {
      setSelectedPlaylist(playlistId);
      loadPlaylistTunes(playlistId);
      return;
    }
    setSelectedPlaylist('all');
    setPlaylistTunes([]);
  }, [mode, playlistId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tunes;
    return tunes.filter((t) =>
      [t.title, t.artist, t.originalName].some((field) =>
        (field || '').toLowerCase().includes(term)
      )
    );
  }, [tunes, search]);

  const filteredMine = useMemo(
    () => filtered.filter((t) => !!currentUserId && String(t.uploadedBy?._id || '') === currentUserId),
    [filtered, currentUserId]
  );

  const filteredOthers = useMemo(
    () => filtered.filter((t) => !currentUserId || String(t.uploadedBy?._id || '') !== currentUserId),
    [filtered, currentUserId]
  );

  const filteredPlaylists = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return playlists;
    return playlists.filter((pl) =>
      [pl.name].some((field) => (field || '').toLowerCase().includes(term))
    );
  }, [playlists, search]);

  const filteredPlaylist = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = playlistTunes || [];
    if (!term) return base;
    return base.filter((t) =>
      [t.title, t.artist, t.originalName].some((field) =>
        (field || '').toLowerCase().includes(term)
      )
    );
  }, [playlistTunes, search]);

  const isPlaylistDetail = mode === 'playlists' && !!playlistId;
  const allDisplayedTunes =
    mode === 'playlist'
      ? filteredPlaylist
      : isPlaylistDetail
        ? filteredPlaylist
        : mode === 'mine'
          ? filteredMine
          : filteredOthers;
  const tunesTotalPages = Math.max(1, Math.ceil(allDisplayedTunes.length / rowsPerPage));
  const currentTunesPage = Math.min(page, tunesTotalPages);
  const paginatedTunes = useMemo(() => {
    const start = (currentTunesPage - 1) * rowsPerPage;
    return allDisplayedTunes.slice(start, start + rowsPerPage);
  }, [allDisplayedTunes, currentTunesPage, rowsPerPage]);
  const playlistTotalPages = Math.max(1, Math.ceil(filteredPlaylists.length / rowsPerPage));
  const currentPlaylistPage = Math.min(page, playlistTotalPages);
  const paginatedPlaylists = useMemo(() => {
    const start = (currentPlaylistPage - 1) * rowsPerPage;
    return filteredPlaylists.slice(start, start + rowsPerPage);
  }, [filteredPlaylists, currentPlaylistPage, rowsPerPage]);
  const playlistViewCount = mode === 'playlists' && !isPlaylistDetail ? filteredPlaylists.length : allDisplayedTunes.length;
  const currentIndex = current && !(mode === 'playlists' && !isPlaylistDetail)
    ? allDisplayedTunes.findIndex((tune) => tune._id === current._id)
    : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allDisplayedTunes.length - 1;
  const currentTitle = current?.title || current?.originalName || current?.filename || 'Untitled track';
  const currentSubtitle = current?.artist || current?.originalName || current?.filename || '';
  const activePlaylistName = playlistId
    ? (playlists.find((pl) => pl._id === playlistId)?.name || 'Playlist')
    : 'Playlist';
  const emptyStateMessage = mode === 'playlist'
    ? (playlists.length === 0
      ? 'No playlists yet. Create one to start building your shelf.'
      : selectedPlaylist === 'all'
        ? 'No tunes found in your playlists yet.'
        : 'This playlist is empty. Add a tune from Browse.'
    )
    : isPlaylistDetail
      ? 'This playlist is empty. Add tunes to see them here.'
    : mode === 'mine'
      ? 'No tunes uploaded by you yet.'
      : mode === 'others'
        ? 'No tunes uploaded by other users found.'
        : 'No playlists found.';

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setError('');
    if (!f) return;
    if (!title.trim()) setTitle(f.name.replace(/\.[^/.]+$/, ''));
    let fileErr = '';
    if (!/\.mp3$/i.test(f.name) && !/audio\/(mpeg|mp3)/i.test(f.type)) fileErr = 'Please choose an MP3 file';
    else if (tuneQuota?.sizeLimit != null && f.size > tuneQuota.sizeLimit * 1024 * 1024) {
      fileErr = getFileSizeLimitMessage(tuneQuota.sizeLimit);
    }
    setUploadErrors((p) => ({ ...p, file: fileErr || undefined, title: undefined }));
  };

  const closeDialog = () => {
    setUploadOpen(false);
    setError('');
    setUploadErrors({});
    setTitle('');
    setArtist('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (tuneLimitReached && tuneQuota?.limit != null) {
      setError(getQuotaLimitReachedMessage(tuneQuota.limit, 'tune'));
      return;
    }
    const errs: { title?: string; artist?: string; file?: string } = {};
    if (!title.trim()) errs.title = 'Song title is required';
    else if (title.trim().length > 100) errs.title = 'Title must be 100 characters or fewer';
    if (artist.trim().length > 100) errs.artist = 'Artist must be 100 characters or fewer';
    if (!file) errs.file = 'Select an MP3 file to upload';
    else if (!/\.mp3$/i.test(file.name) && !/audio\/(mpeg|mp3)/i.test(file.type)) errs.file = 'Please choose an MP3 file';
    else if (tuneQuota?.sizeLimit != null && file.size > tuneQuota.sizeLimit * 1024 * 1024) {
      errs.file = getFileSizeLimitMessage(tuneQuota.sizeLimit);
    }
    setUploadErrors(errs);
    if (Object.keys(errs).length || !file) return;

    setUploading(true);
    try {
      await uploadTune({ title, artist, file });
      setTitle('');
      setArtist('');
      setFile(null);
      setError('');
      setUploadOpen(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      Swal.fire('Uploaded', 'Tune added to your library', 'success');
      await load(search); // refreshes quota count too
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to upload tune';
      setError(msg);
      Swal.fire('Error', msg, 'error');
    } finally {
      setUploading(false);
    }
  };

  const showNetworkIssueMessage = () => {
    const audio = audioRef.current;
    if (isSeekingRef.current || audio?.seeking) return;
    setPlaybackStatusMessage(
      navigator.onLine
        ? 'Connection unstable. Auto-resuming playback...'
        : 'Offline. Auto-resuming playback...'
    );
  };

  const clearPlaybackIssueMessage = () => {
    setPlaybackStatusMessage('');
  };

  // When the audio element enters an error state (MEDIA_ERR_NETWORK), plain play()
  // will always fail. We must call load() to reset the element, then seek back and play.
  const reloadAndResume = () => {
    const audio = audioRef.current;
    if (!audio || !intendedPlaybackRef.current || isReloadingRef.current) return;
    isReloadingRef.current = true;
    const savedTime = savedTimeRef.current;
    audio.load();
    const finish = () => {
      isReloadingRef.current = false;
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('error', onErr);
    };
    const onCanPlay = () => {
      if (!intendedPlaybackRef.current) { finish(); return; }
      if (savedTime > 0) audio.currentTime = savedTime;
      audio.play().then(clearPlaybackIssueMessage).catch(() => {});
      finish();
    };
    const onErr = () => finish();
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('error', onErr);
  };

  const resumePlaybackIfNeeded = () => {
    const audio = audioRef.current;
    if (!audio || !current || !intendedPlaybackRef.current) return;
    if (audio.error) {
      reloadAndResume();
      return;
    }
    audio.play().then(clearPlaybackIssueMessage).catch(() => {});
  };

  const playTune = (tune: TuneItem) => {
    const audio = audioRef.current;

    // Switch to a different track
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    savedTimeRef.current = 0;
    isReloadingRef.current = false;
    intendedPlaybackRef.current = true;
    setCurrent(tune);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    clearPlaybackIssueMessage();
    setTimeout(() => {
      audioRef.current?.play().catch(() => {});
    }, 20);
  };

  const handlePlay = (tune: TuneItem) => {
    const audio = audioRef.current;

    // Toggle play/pause if the same track is selected
    if (current?._id === tune._id && audio) {
      if (audio.paused) {
        intendedPlaybackRef.current = true;
        clearPlaybackIssueMessage();
        audio.play().catch(() => {});
      } else {
        intendedPlaybackRef.current = false;
        clearPlaybackIssueMessage();
        audio.pause();
      }
      return;
    }

    playTune(tune);
  };

  const handleStepTrack = (direction: 'previous' | 'next') => {
    if (currentIndex < 0) return;
    const nextIndex = direction === 'previous' ? currentIndex - 1 : currentIndex + 1;
    const nextTune = allDisplayedTunes[nextIndex];
    if (!nextTune) return;
    playTune(nextTune);
  };

  const handlePlayerToggle = () => {
    const audio = audioRef.current;
    if (!audio || !current) return;

    if (audio.paused) {
      intendedPlaybackRef.current = true;
      clearPlaybackIssueMessage();
      audio.play().catch(() => {});
      return;
    }

    intendedPlaybackRef.current = false;
    clearPlaybackIssueMessage();
    audio.pause();
  };

  const handleClosePlayer = () => {
    const audio = audioRef.current;
    intendedPlaybackRef.current = false;
    isSeekingRef.current = false;
    savedTimeRef.current = 0;
    isReloadingRef.current = false;
    clearPlaybackIssueMessage();
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setCurrent(null);
  };

  const handleSeekChange = (_: Event, value: number | number[]) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    isSeekingRef.current = true;
    setCurrentTime(nextValue);
    clearPlaybackIssueMessage();
  };

  const handleSeekCommit = (_: Event | React.SyntheticEvent, value: number | number[]) => {
    const audio = audioRef.current;
    const nextValue = Array.isArray(value) ? value[0] : value;
    if (audio) {
      audio.currentTime = nextValue;
    }
    isSeekingRef.current = false;
    if (intendedPlaybackRef.current) resumePlaybackIfNeeded();
  };

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const vol = Array.isArray(value) ? value[0] : value;
    setTuneVolume(vol);
    setTuneMuted(vol === 0);
    const audio = audioRef.current;
    if (audio) {
      audio.volume = vol;
      audio.muted = vol === 0;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (tuneMuted) {
      const restore = volumeBeforeMuteRef.current || 1;
      setTuneMuted(false);
      setTuneVolume(restore);
      if (audio) { audio.muted = false; audio.volume = restore; }
    } else {
      volumeBeforeMuteRef.current = tuneVolume;
      setTuneMuted(true);
      setTuneVolume(0);
      if (audio) { audio.muted = true; }
    }
  };

  const getVolumeIcon = () => {
    if (tuneMuted || tuneVolume === 0) return <VolumeMuteRounded fontSize="small" />;
    if (tuneVolume <= 0.45) return <VolumeDownRounded fontSize="small" />;
    return <VolumeUpRounded fontSize="small" />;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio && current) {
      audio.volume = tuneMuted ? 0 : tuneVolume;
      audio.muted = tuneMuted;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    const handleOffline = () => {
      if (current && intendedPlaybackRef.current) showNetworkIssueMessage();
    };
    const handleOnline = () => {
      if (current && intendedPlaybackRef.current) resumePlaybackIfNeeded();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [current]);

  useEffect(() => {
    if (!current || !playbackStatusMessage) return;

    const timer = window.setInterval(() => {
      const audio = audioRef.current;
      if (!audio || !intendedPlaybackRef.current || !navigator.onLine) return;

      if (audio.error) {
        // Audio entered an error state — must reload before playing
        reloadAndResume();
      } else if (audio.readyState >= 3 && audio.paused) {
        clearPlaybackIssueMessage();
        audio.play().catch(() => {});
      }
    }, 800);

    return () => window.clearInterval(timer);
  }, [current, playbackStatusMessage]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    if (!current) {
      navigator.mediaSession.metadata = null;
      return;
    }

    if ('MediaMetadata' in window) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTitle,
        artist: currentSubtitle,
        album: 'QuillStack Tunes',
      });
    }

    navigator.mediaSession.setActionHandler('play', () => {
      intendedPlaybackRef.current = true;
      audioRef.current?.play().catch(() => {});
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      intendedPlaybackRef.current = false;
      audioRef.current?.pause();
    });
    navigator.mediaSession.setActionHandler('previoustrack', hasPrevious
      ? () => handleStepTrack('previous')
      : null);
    navigator.mediaSession.setActionHandler('nexttrack', hasNext
      ? () => handleStepTrack('next')
      : null);

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
    };
  }, [current, currentTitle, currentSubtitle, hasNext, hasPrevious]);

  const savePlaylistAction = async () => {
    const nm = playlistName.trim();
    if (!nm) {
      setPlaylistError('Name is required');
      return;
    }
    try {
      if (editingPlaylist) {
        await updatePlaylist(editingPlaylist._id, nm);
      } else {
        await createPlaylist(nm);
      }
      setPlaylistDlgOpen(false);
      setPlaylistName('');
      setPlaylistError('');
      setEditingPlaylist(null);
      await loadPlaylists();
      goToPlaylists();
      Swal.fire(
        'Saved',
        editingPlaylist ? 'Playlist updated successfully.' : 'Playlist created successfully.',
        'success'
      );
    } catch (e: any) {
      const msg = e?.response?.data?.message || (editingPlaylist ? 'Failed to update playlist' : 'Failed to create playlist');
      setPlaylistError(msg);
      Swal.fire('Error', msg, 'error');
    }
  };

  const removePlaylist = async (playlist: PlaylistItem) => {
    const result = await Swal.fire({
      title: 'Delete playlist?',
      text: `This will remove "${playlist.name}" from your playlists.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    try {
      await deletePlaylist(playlist._id);
      await loadPlaylists();
      if (selectedPlaylist === playlist._id) {
        setSelectedPlaylist('all');
        setPlaylistTunes([]);
      }
      if (mode === 'playlist') {
        await loadPlaylistTunes(selectedPlaylist);
      }
      if (playlistId === playlist._id) {
        goToPlaylists();
      }
      Swal.fire('Deleted', 'Playlist deleted successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete playlist';
      Swal.fire('Error', msg, 'error');
    }
  };

  const openAddToPlaylist = (tune: TuneItem) => {
    if (!playlists.length) {
      Swal.fire('No playlists', 'Create a playlist first.', 'info');
      openCreatePlaylist();
      return;
    }
    setPlaylistTarget(tune);
    const defaultChoice = selectedPlaylist !== 'all'
      ? selectedPlaylist
      : (playlists[0]?._id || '');
    setPlaylistChoice(defaultChoice);
    setPlaylistChoiceError('');
    setPlaylistSelectOpen(true);
  };

  const confirmAddToPlaylist = async () => {
    if (!playlistTarget) {
      setPlaylistSelectOpen(false);
      return;
    }
    const pid = playlistChoice;
    if (!pid) {
      setPlaylistChoiceError('Select a playlist');
      return;
    }
    try {
      await addTuneToPlaylist(pid, playlistTarget._id);
      setPlaylistSelectOpen(false);
      setSelectedPlaylist(pid);
      await loadPlaylistTunes(pid);
      openPlaylistSongs(pid);
      Swal.fire('Added', 'Song added to playlist', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to add to playlist';
      setPlaylistChoiceError(msg);
      Swal.fire('Error', msg, 'error');
    }
  };

  const canDeleteTune = (tune: TuneItem) =>
    !!currentUserId && String(tune.uploadedBy?._id || '') === currentUserId;

  const saveTuneEdit = async () => {
    const nextTitle = editTuneTitle.trim();
    if (!editingTune) return;
    const errs: { title?: string; artist?: string } = {};
    if (!nextTitle) errs.title = 'Song name is required';
    else if (nextTitle.length > 100) errs.title = 'Song name must be 100 characters or fewer';
    if (editTuneArtist.trim().length > 100) errs.artist = 'Artist must be 100 characters or fewer';
    setEditTuneErrors(errs);
    if (Object.keys(errs).length) return;

    setSavingTuneEdit(true);
    try {
      const updated = await updateTune(editingTune._id, {
        title: nextTitle,
        artist: editTuneArtist.trim(),
      });

      setTunes((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
      setPlaylistTunes((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
      setCurrent((prev) => (prev?._id === updated._id ? updated : prev));
      setTuneEditOpen(false);
      setEditingTune(null);
      setEditTuneTitle('');
      setEditTuneArtist('');
      setEditTuneError('');
      Swal.fire('Saved', 'Song updated successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to update song';
      setEditTuneError(msg);
      Swal.fire('Error', msg, 'error');
    } finally {
      setSavingTuneEdit(false);
    }
  };

  const handleDeleteTune = async (tune: TuneItem) => {
    const result = await Swal.fire({
      title: 'Delete tune?',
      text: 'This will permanently delete the tune and remove its MP3 file from storage.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    setDeletingTuneId(tune._id);
    try {
      await deleteTune(tune._id);

      if (current?._id === tune._id && audioRef.current) {
        intendedPlaybackRef.current = false;
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setCurrent(null);
        setIsPlaying(false);
        clearPlaybackIssueMessage();
      }

      setTunes((prev) => prev.filter((item) => item._id !== tune._id));
      setPlaylistTunes((prev) => prev.filter((item) => item._id !== tune._id));
      setTuneQuota((prev) => prev ? { ...prev, count: Math.max(0, prev.count - 1) } : prev);

      Swal.fire('Deleted', 'Tune deleted successfully.', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to delete tune';
      Swal.fire('Error', msg, 'error');
    } finally {
      setDeletingTuneId(null);
    }
  };

  // Called by the bulk-delete bar after deleteTune() succeeded for these ids.
  const handleBulkTunesDeleted = (ids: string[]) => {
    if (!ids.length) return;
    if (current && ids.includes(current._id) && audioRef.current) {
      intendedPlaybackRef.current = false;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrent(null);
      setIsPlaying(false);
      clearPlaybackIssueMessage();
    }
    setTunes((prev) => prev.filter((item) => !ids.includes(item._id)));
    setPlaylistTunes((prev) => prev.filter((item) => !ids.includes(item._id)));
    setTuneQuota((prev) => (prev ? { ...prev, count: Math.max(0, prev.count - ids.length) } : prev));
  };

  return (
    <Box>
      <PageHeader
        title="Tunes"
        subtitle="Your tunes shelf, own uploads, and others' uploads in one place."
        actions={
          <Button
            variant="contained"
            startIcon={<CloudUploadRounded />}
            onClick={() => { setUploadOpen(true); setError(''); }}
            disabled={tuneLimitReached}
          >
            Upload MP3
          </Button>
        }
      />

      <Paper
        elevation={0}
        sx={{ mb: 2.25, p: 0, background: 'transparent', boxShadow: 'none', border: 0, overflow: 'visible' }}
      >
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <TabFilterBar
              noMargin
              value={mode === 'playlist' ? 'tunes' : mode === 'mine' ? 'own' : mode === 'others' ? 'others' : 'playlists'}
              onChange={(v) => {
                if (v === 'tunes') goToTunes();
                else if (v === 'own') goToOwn();
                else if (v === 'others') goToOthers();
                else goToPlaylists();
              }}
              options={[
                { value: 'tunes', label: 'Tunes' },
                { value: 'own', label: 'Own' },
                { value: 'others', label: 'Others' },
                { value: 'playlists', label: 'Playlists' },
              ]}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                icon={<QueueMusicRounded />}
                label={
                  mode === 'playlists' && !isPlaylistDetail
                    ? `${playlistViewCount} playlist${playlistViewCount === 1 ? '' : 's'}`
                    : `${allDisplayedTunes.length} track${allDisplayedTunes.length === 1 ? '' : 's'}`
                }
                sx={{ bgcolor: alpha(BRAND_PURPLE, 0.12), color: BRAND_PURPLE, fontWeight: 800, '& .MuiChip-icon': { color: 'inherit' } }}
              />
              {tuneQuota?.limit != null && (
                <Chip
                  label={`${tuneQuota.count} / ${tuneQuota.limit} uploads`}
                  sx={{
                    bgcolor: tuneQuota.count >= tuneQuota.limit ? alpha('#d32f2f', 0.12) : alpha('#f59e0b', 0.14),
                    color: tuneQuota.count >= tuneQuota.limit ? '#b3261e' : '#b45309',
                    fontWeight: 800,
                  }}
                />
              )}
              {current && (
                <Chip
                  icon={<MusicNoteRounded />}
                  label={`Now playing: ${currentTitle}`}
                  sx={{
                    maxWidth: 340, fontWeight: 800, color: '#fff',
                    background: 'linear-gradient(135deg, #4776E6, #8E54E9)',
                    boxShadow: '0 8px 18px rgba(71,118,230,.4)',
                    '& .MuiChip-icon': { color: '#fff' },
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Paper
            sx={{
              p: 1.75,
              borderRadius: '22px',
              border: '1px solid',
              borderColor: alpha(BRAND_PURPLE, 0.2),
              bgcolor: 'background.paper',
              boxShadow: `0 10px 26px ${alpha(BRAND_PURPLE, 0.1)}`,
            }}
          >
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  mode === 'playlists' && !isPlaylistDetail
                      ? 'Search playlists…'
                      : mode === 'playlist' || isPlaylistDetail
                      ? 'Search within playlist…'
                      : mode === 'mine'
                        ? 'Search your uploads…'
                        : 'Search other uploads…'
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRounded sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                  },
                }}
              />
              <ViewToggle value={viewMode} onChange={setViewMode} />
              <FormControl size="small" sx={{ minWidth: 140, flex: { xs: '1 1 auto', md: '0 0 140px' } }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  label="Per page"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[5, 10, 20, 30, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                {mode === 'playlist' && (
                  <TextField
                    select
                    label="Playlist"
                    size="small"
                    sx={{
                      minWidth: { md: 230 },
                      width: { xs: '100%', md: 'auto' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                      },
                    }}
                    value={selectedPlaylist}
                    onChange={(e) => selectPlaylist(e.target.value)}
                    helperText={playlists.length === 0 ? 'Create a playlist to get started' : ''}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {playlists.map((pl) => (
                      <MenuItem key={pl._id} value={pl._id}>{pl.name}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
              {loading && <LinearProgress sx={{ borderRadius: 1, height: 6 }} />}
            </Stack>
          </Paper>
        </Stack>
      </Paper>

        {mode === 'playlists' && !isPlaylistDetail ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: alpha(BRAND_PURPLE, 0.12),
              bgcolor: 'background.paper',
            }}
          >
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Box>
                  <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: '-0.02em' }}>
                    Your Playlists
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Open a playlist to view the tunes inside it.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<PlaylistAddRounded />}
                  onClick={openCreatePlaylist}
                  sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700, px: 2 }}
                >
                  New Playlist
                </Button>
              </Stack>

              {filteredPlaylists.length === 0 ? (
                <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', bgcolor: alpha(BRAND_PURPLE, 0.03) }}>
                  <Typography color="text.secondary">
                    {playlists.length === 0
                      ? 'No playlists yet. Create one to get started.'
                      : 'No playlists match your search.'}
                  </Typography>
                </Paper>
              ) : (
                <>
                <BulkSelectBar
                  bulk={playlistBulk}
                  visibleIds={paginatedPlaylists.map((pl) => pl._id)}
                  noun="playlist"
                  deleteOne={deletePlaylist}
                  onDone={async (ids) => {
                    await loadPlaylists();
                    if (ids.includes(selectedPlaylist)) {
                      setSelectedPlaylist('all');
                      setPlaylistTunes([]);
                    }
                  }}
                />
                <Box
                  sx={viewMode === 'grid'
                    ? { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' } }
                    : { display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                  {paginatedPlaylists.map((pl, idx) => {
                    const tuneCount = Array.isArray(pl.tunes) ? pl.tunes.length : 0;
                    const updatedAt = pl.updatedAt ? formatDate(pl.updatedAt) : '—';
                    const grid = viewMode === 'grid';
                    return (
                      <NoteStyleCard
                        key={pl._id}
                        accent="#4776E6"
                        accent2="#8E54E9"
                        grid={grid}
                        index={idx}
                        selected={playlistBulk.isSelected(pl._id)}
                        watermark={QueueMusicRounded}
                        onClick={() => openPlaylistSongs(pl._id)}
                        header={
                          <>
                            <GlassIconTile size={grid ? 46 : 52}><QueueMusicRounded /></GlassIconTile>
                            <Box sx={{ textAlign: grid ? 'right' : 'center' }}>
                              <Typography sx={{ fontWeight: 900, fontSize: grid ? 26 : 20, lineHeight: 1.1, textShadow: '0 2px 8px rgba(0,0,0,.25)' }}>
                                {tuneCount}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 800, opacity: 0.95 }}>
                                {tuneCount === 1 ? 'track' : 'tracks'}
                              </Typography>
                            </Box>
                          </>
                        }
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={1} onClick={(e) => e.stopPropagation()}>
                          <Checkbox size="small" checked={playlistBulk.isSelected(pl._id)} onChange={() => playlistBulk.toggle(pl._id)} sx={{ p: 0.25, ml: -0.5 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.3 }} noWrap title={pl.name}>
                            {pl.name}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">Updated {updatedAt || '—'}</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" spacing={0.5} justifyContent="space-between" alignItems="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<VisibilityRounded />}
                            onClick={() => openPlaylistSongs(pl._id)}
                            sx={{ borderRadius: '999px' }}
                          >
                            Open
                          </Button>
                          <Stack direction="row" spacing={0.25}>
                            <IconButton size="small" onClick={() => openEditPlaylist(pl)} aria-label={`Edit playlist ${pl.name}`}>
                              <EditRounded fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => removePlaylist(pl)} aria-label={`Delete playlist ${pl.name}`}>
                              <DeleteOutlineRounded fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </NoteStyleCard>
                    );
                  })}
                </Box>
                </>
              )}
              {filteredPlaylists.length > 0 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={playlistTotalPages}
                    page={currentPlaylistPage}
                    onChange={(_e, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                  />
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      ) : (
      <Box
        sx={viewMode === 'grid'
          ? { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' } }
          : { display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {isPlaylistDetail && (
          <Paper
            sx={{
              gridColumn: '1 / -1',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: alpha(BRAND_PURPLE, 0.12),
              bgcolor: alpha(BRAND_PURPLE, 0.03),
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={900} sx={{ letterSpacing: '-0.02em' }}>
                  {activePlaylistName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Songs in this playlist, shown without leaving /tunes.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={goToPlaylists}
                sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700 }}
              >
                Back to List
              </Button>
            </Stack>
          </Paper>
        )}
        {loading ? (
          Array.from({ length: 6 }).map((_, idx) => (
            <Paper key={idx} sx={{ p: 1.5, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Skeleton variant="rounded" width={60} height={60} sx={{ borderRadius: 1, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Skeleton height={26} width="52%" />
                  <Skeleton height={18} width="34%" />
                  <Skeleton height={18} width="66%" />
                </Box>
                <Stack direction="row" spacing={1}>
                  <Skeleton variant="rounded" width={96} height={36} />
                  <Skeleton variant="rounded" width={120} height={36} />
                </Stack>
              </Stack>
            </Paper>
          ))
        ) : allDisplayedTunes.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 1, textAlign: 'center', gridColumn: '1 / -1' }}>
            <Typography color="text.secondary">
              {emptyStateMessage}
            </Typography>
          </Paper>
        ) : (
          [
          <Box key="tune-bulk-bar" sx={{ gridColumn: '1 / -1' }}>
            <BulkSelectBar
              bulk={tuneBulk}
              visibleIds={paginatedTunes.filter(canDeleteTune).map((t) => t._id)}
              noun="tune"
              deleteOne={deleteTune}
              onDone={handleBulkTunesDeleted}
            />
          </Box>,
          ...paginatedTunes.map((tune) => {
            const src = absoluteUrl(tune.fileUrl);
            const isCurrent = current?._id === tune._id;
            const isCurrentPlaying = isCurrent && isPlaying;
            const metaParts = [
              tune.artist || '',
              formatDate(tune.createdAt),
              formatBytes(tune.size),
            ].filter(Boolean);
            const detailParts = [
              tune.originalName || tune.filename || '',
              mode === 'playlist'
                ? ''
                : mode === 'mine'
                  ? 'Uploaded by You'
                  : `Uploaded by ${displayUploader(tune)}`,
            ].filter(Boolean);
            return (
              <Paper
                key={tune._id}
                sx={{
                  p: 1.5,
                  borderRadius: '24px',
                  border: '1px solid',
                  borderColor: isCurrent ? '#4776E6' : alpha('#4776E6', 0.2),
                  background: (t) => isCurrent
                    ? `linear-gradient(135deg, ${alpha('#4776E6', 0.14)} 0%, ${alpha('#8E54E9', 0.12)} 100%)`
                    : t.palette.background.paper,
                  boxShadow: isCurrent
                    ? '0 20px 40px rgba(71, 118, 230, 0.30)'
                    : '0 12px 26px rgba(71, 118, 230, 0.14)',
                  transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 24px 44px rgba(71, 118, 230, 0.30)',
                  },
                }}
              >
                <Stack
                  direction={viewMode === 'grid' ? 'column' : 'row'}
                  spacing={1.5}
                  alignItems={viewMode === 'grid' ? 'stretch' : 'center'}
                  sx={{ minWidth: 0 }}
                >
                    <Box
                      sx={{
                        width: viewMode === 'grid' ? '100%' : 58,
                        height: viewMode === 'grid' ? 120 : 58,
                      borderRadius: viewMode === 'grid' ? '18px' : '16px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      bgcolor: '#0f172a',
                      color: '#fff',
                      background: isCurrent
                        ? 'linear-gradient(135deg, #4776E6 0%, #8E54E9 60%, #F6A938 130%)'
                        : 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                      boxShadow: isCurrent
                        ? '0 16px 28px rgba(71, 118, 230, 0.4)'
                        : '0 10px 20px rgba(71, 118, 230, 0.25), inset 0 1px 0 rgba(255,255,255,0.35)',
                      '&::before': {
                        content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
                        background: 'linear-gradient(135deg, rgba(255,255,255,.4) 0%, rgba(255,255,255,.06) 45%, rgba(255,255,255,0) 46%, rgba(0,0,0,.12) 100%)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 7,
                        borderRadius: 1,
                        border: `1px solid ${alpha('#fff', 0.18)}`,
                      }}
                    />
                    <MusicNoteRounded sx={{ fontSize: viewMode === 'grid' ? 48 : 28, position: 'relative', zIndex: 1 }} />
                    {viewMode === 'grid' && (
                      <MusicNoteRounded sx={{ position: 'absolute', right: -10, bottom: -14, fontSize: 110, color: 'rgba(255,255,255,.18)', transform: 'rotate(-14deg)' }} />
                    )}
                  </Box>

                  <Stack spacing={0.55} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction={viewMode === 'grid' ? 'column' : { xs: 'column', md: 'row' }}
                      spacing={0.75}
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={900}
                        noWrap
                        sx={{ minWidth: 0, color: isCurrent ? BRAND_PURPLE_DARK : 'text.primary', letterSpacing: '-0.02em' }}
                      >
                        {tune.title || tune.originalName || tune.filename || 'Untitled track'}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {isCurrent && (
                          <Chip
                            size="small"
                            label={isCurrentPlaying ? 'Now Playing' : 'Paused'}
                            sx={{
                              height: 24,
                              bgcolor: alpha(BRAND_PURPLE, 0.12),
                              color: BRAND_PURPLE,
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={formatBytes(tune.size)}
                          sx={{ height: 24, bgcolor: 'action.hover', fontWeight: 700 }}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      {tune.artist && <Chip size="small" label={tune.artist} variant="outlined" sx={{ height: 24 }} />}
                      {metaParts.length > 0 && (
                        <Typography variant="caption" color="text.secondary" noWrap title={metaParts.join(' • ')}>
                          {metaParts.join(' • ')}
                        </Typography>
                      )}
                    </Stack>

                    {detailParts.length > 0 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ minWidth: 0 }}
                        title={detailParts.join(' • ')}
                      >
                        {detailParts.join(' • ')}
                      </Typography>
                    )}
                  </Stack>

                  <Stack
                    direction={viewMode === 'grid' ? 'row' : { xs: 'column', sm: 'row' }}
                    spacing={0.75}
                    alignItems="center"
                    useFlexGap
                    flexWrap={viewMode === 'grid' ? 'wrap' : undefined}
                    sx={{ flexShrink: 0, width: { xs: '100%', sm: viewMode === 'grid' ? '100%' : 'auto' } }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => handlePlay(tune)}
                      disabled={!src}
                      startIcon={isCurrentPlaying ? <PauseRounded /> : <PlayArrowRounded />}
                      sx={{
                        minWidth: viewMode === 'grid' ? 0 : { xs: '100%', sm: 118 },
                        flex: viewMode === 'grid' ? '1 1 auto' : undefined,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      {isCurrentPlaying ? 'Pause' : 'Play'}
                    </Button>
                    {mode !== 'playlist' && (
                      <Button
                        variant="outlined"
                        onClick={() => openAddToPlaylist(tune)}
                        size="small"
                        sx={{
                          minWidth: { xs: '100%', sm: 144 },
                          borderRadius: 1,
                          textTransform: 'none',
                          px: 1.75,
                          whiteSpace: 'nowrap',
                          fontWeight: 700,
                        }}
                      >
                        Add to Playlist
                      </Button>
                    )}
                    {mode === 'mine' && canDeleteTune(tune) && (
                      <IconButton
                        size="small"
                        onClick={() => openEditTune(tune)}
                        sx={{
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: alpha(BRAND_PURPLE, 0.18),
                          bgcolor: alpha(BRAND_PURPLE, 0.04),
                        }}
                        aria-label="Edit tune"
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                    )}
                    {canDeleteTune(tune) && (
                      <Checkbox
                        size="small"
                        checked={tuneBulk.isSelected(tune._id)}
                        onChange={() => tuneBulk.toggle(tune._id)}
                        inputProps={{ 'aria-label': 'Select tune' }}
                      />
                    )}
                    {canDeleteTune(tune) && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTune(tune)}
                        disabled={deletingTuneId === tune._id}
                        sx={{
                          flexShrink: 0,
                          border: '1px solid',
                          borderColor: alpha('#d32f2f', 0.18),
                          bgcolor: alpha('#d32f2f', 0.04),
                        }}
                        aria-label="Delete tune"
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          }),
          ]
        )}
        {allDisplayedTunes.length > 0 && (
          <Box display="flex" justifyContent="center" mt={3} sx={{ gridColumn: '1 / -1' }}>
            <Pagination
              count={tunesTotalPages}
              page={currentTunesPage}
              onChange={(_e, p) => setPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>
      )}

      {/* ===== Upload ===== */}
      <FormDialogShell
        open={uploadOpen}
        onClose={closeDialog}
        colors={TUNES_COLORS}
        icon={<MusicNoteRounded />}
        title="Upload a new track"
        subtitle="Add an MP3 to your tunes shelf"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={closeDialog} sx={tunesCancelSx}>Cancel</Button>
            <Button
              onClick={() => submit()}
              disabled={uploading || !!error}
              startIcon={<CloudUploadRounded />}
              sx={gradientButtonSx(TUNES_COLORS)}
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </>
        }
      >
        <Stack spacing={2.25}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Box>
              <Typography sx={tunesLabelSx}>Title</Typography>
              <TextField
                placeholder="Song title"
                fullWidth
                value={title}
                onChange={(e) => { setTitle(e.target.value); setUploadErrors((p) => ({ ...p, title: undefined })); }}
                error={!!uploadErrors.title}
                helperText={uploadErrors.title}
                sx={tunesInputSx}
              />
            </Box>
            <Box>
              <Typography sx={tunesLabelSx}>Artist</Typography>
              <TextField
                placeholder="Who performed it?"
                fullWidth
                value={artist}
                onChange={(e) => { setArtist(e.target.value); setUploadErrors((p) => ({ ...p, artist: undefined })); }}
                error={!!uploadErrors.artist}
                helperText={uploadErrors.artist}
                sx={tunesInputSx}
              />
            </Box>
          </Box>

          {/* file picker */}
          <Box
            component="label"
            sx={{
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2, p: 2.25, borderRadius: '20px',
              border: '2px dashed', borderColor: file ? '#4776E6' : 'rgba(71,118,230,.45)',
              bgcolor: file ? 'rgba(71,118,230,.08)' : 'transparent',
              transition: 'all .2s ease',
              '&:hover': { bgcolor: 'rgba(71,118,230,.08)', borderColor: '#4776E6' },
            }}
          >
            <Box
              sx={{
                width: 56, height: 56, borderRadius: '18px', display: 'grid', placeItems: 'center', color: '#fff', flexShrink: 0,
                background: 'linear-gradient(135deg,#4776E6,#8E54E9)',
                boxShadow: '0 10px 20px rgba(71,118,230,.4), inset 0 1px 0 rgba(255,255,255,.45)',
              }}
            >
              {file ? <MusicNoteRounded /> : <CloudUploadRounded />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 900 }} noWrap>{file ? file.name : 'Choose an MP3 file'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {file
                  ? 'Click to choose a different file'
                  : tuneQuota?.sizeLimit != null ? `MP3 only · up to ${tuneQuota.sizeLimit} MB` : 'MP3 only'}
              </Typography>
            </Box>
            <input ref={fileInputRef} hidden type="file" accept="audio/mpeg,audio/mp3" onChange={onFileChange} />
          </Box>
          {uploadErrors.file && (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mt: -1 }}>{uploadErrors.file}</Typography>
          )}

          {error && (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>{error}</Typography>
          )}
        </Stack>
      </FormDialogShell>

      {/* ===== Edit song ===== */}
      <FormDialogShell
        open={tuneEditOpen}
        onClose={() => setTuneEditOpen(false)}
        colors={TUNES_COLORS}
        icon={<EditRounded />}
        title="Edit Song"
        subtitle="Update the name and artist"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => {
                setTuneEditOpen(false);
                setEditingTune(null);
                setEditTuneTitle('');
                setEditTuneArtist('');
                setEditTuneError('');
                setEditTuneErrors({});
              }}
              sx={tunesCancelSx}
            >
              Cancel
            </Button>
            <Button
              onClick={saveTuneEdit}
              disabled={savingTuneEdit}
              sx={gradientButtonSx(TUNES_COLORS)}
            >
              {savingTuneEdit ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <Stack spacing={2.25}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <Box>
              <Typography sx={tunesLabelSx}>Song name</Typography>
              <TextField
                fullWidth
                autoFocus
                value={editTuneTitle}
                onChange={(e) => { setEditTuneTitle(e.target.value); setEditTuneError(''); setEditTuneErrors((p) => ({ ...p, title: undefined })); }}
                error={!!editTuneErrors.title}
                helperText={editTuneErrors.title}
                sx={tunesInputSx}
              />
            </Box>
            <Box>
              <Typography sx={tunesLabelSx}>Artist</Typography>
              <TextField
                fullWidth
                value={editTuneArtist}
                onChange={(e) => { setEditTuneArtist(e.target.value); setEditTuneError(''); setEditTuneErrors((p) => ({ ...p, artist: undefined })); }}
                error={!!editTuneErrors.artist}
                helperText={editTuneErrors.artist}
                sx={tunesInputSx}
              />
            </Box>
          </Box>
          {editTuneError && (
            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>{editTuneError}</Typography>
          )}
        </Stack>
      </FormDialogShell>

      {/* ===== Create / edit playlist ===== */}
      <FormDialogShell
        open={playlistDlgOpen}
        onClose={() => {
          setPlaylistDlgOpen(false);
          setEditingPlaylist(null);
        }}
        colors={TUNES_COLORS}
        maxWidth="xs"
        icon={<PlaylistAddRounded />}
        title={editingPlaylist ? 'Edit Playlist' : 'Create Playlist'}
        subtitle="Group your favourite tunes together"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => {
                setPlaylistDlgOpen(false);
                setEditingPlaylist(null);
              }}
              sx={tunesCancelSx}
            >
              Cancel
            </Button>
            <Button onClick={savePlaylistAction} sx={gradientButtonSx(TUNES_COLORS)}>
              {editingPlaylist ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <Typography sx={tunesLabelSx}>Playlist name</Typography>
        <TextField
          fullWidth
          autoFocus
          placeholder="e.g. Morning vibes"
          value={playlistName}
          onChange={(e) => { setPlaylistName(e.target.value); setPlaylistError(''); }}
          error={!!playlistError}
          helperText={playlistError || 'Give your playlist a descriptive name.'}
          sx={tunesInputSx}
        />
      </FormDialogShell>

      {/* ===== Add to playlist ===== */}
      <FormDialogShell
        open={playlistSelectOpen}
        onClose={() => setPlaylistSelectOpen(false)}
        colors={TUNES_COLORS}
        maxWidth="xs"
        icon={<QueueMusicRounded />}
        title="Add to playlist"
        subtitle="Choose where to add this song"
        footer={
          <>
            <Box sx={{ flex: 1 }} />
            <Button onClick={() => setPlaylistSelectOpen(false)} sx={tunesCancelSx}>Cancel</Button>
            <Button onClick={confirmAddToPlaylist} sx={gradientButtonSx(TUNES_COLORS)}>Add</Button>
          </>
        }
      >
        <Typography sx={tunesLabelSx}>Playlist</Typography>
        <TextField
          select
          fullWidth
          value={playlistChoice}
          onChange={(e) => { setPlaylistChoice(e.target.value); setPlaylistChoiceError(''); }}
          helperText={playlistChoiceError || (playlists.length ? 'Pick a playlist from the list.' : 'Create a playlist first.')}
          error={!!playlistChoiceError}
          sx={tunesInputSx}
        >
          {playlists.map((pl) => (
            <MenuItem key={pl._id} value={pl._id}>{pl.name}</MenuItem>
          ))}
        </TextField>
      </FormDialogShell>

      {current && (
        <Paper
          elevation={6}
          sx={{
            position: 'sticky',
            bottom: 0,
            mt: 2,
            borderRadius: 1,
            p: 1,
            backdropFilter: 'blur(14px)',
            background: (t) => t.palette.mode === 'dark'
              ? `radial-gradient(circle at top left, ${alpha(BRAND_PURPLE, 0.26)}, transparent 36%), linear-gradient(180deg, ${t.palette.background.paper}, ${t.palette.background.paper})`
              : `radial-gradient(circle at top left, ${alpha(BRAND_PURPLE, 0.14)}, transparent 36%), linear-gradient(180deg, rgba(255,255,255,0.98), rgba(251,247,255,0.98))`,
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.14)',
          }}
        >
          <Stack spacing={0.75}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    flexShrink: 0,
                    background: `linear-gradient(135deg, ${BRAND_PURPLE} 0%, ${BRAND_PURPLE_DARK} 55%, ${BRAND_ORANGE} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    boxShadow: `0 8px 16px ${alpha(BRAND_PURPLE, 0.24)}`,
                  }}
                >
                  <MusicNoteRounded sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={800} noWrap title={currentTitle} sx={{ letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                    {currentTitle}
                  </Typography>
                  <Typography variant="caption" noWrap title={currentSubtitle} sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
                    {currentSubtitle}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  onClick={() => handleStepTrack('previous')}
                  disabled={!hasPrevious}
                  aria-label="Play previous tune"
                  size="small"
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', width: 34, height: 34 }}
                >
                  <SkipPreviousRounded fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={handlePlayerToggle}
                  aria-label={isPlaying ? 'Pause current tune' : 'Play current tune'}
                  sx={{
                    color: '#fff',
                    bgcolor: BRAND_PURPLE,
                    width: 40,
                    height: 40,
                    boxShadow: `0 8px 16px ${alpha(BRAND_PURPLE, 0.24)}`,
                    '&:hover': { bgcolor: BRAND_PURPLE_DARK },
                  }}
                >
                  {isPlaying ? <PauseRounded /> : <PlayArrowRounded />}
                </IconButton>
                <IconButton
                  onClick={() => handleStepTrack('next')}
                  disabled={!hasNext}
                  aria-label="Play next tune"
                  size="small"
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', width: 34, height: 34 }}
                >
                  <SkipNextRounded fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={toggleMute}
                  aria-label={tuneMuted ? 'Unmute' : 'Mute'}
                  size="small"
                  sx={{
                    color: tuneMuted ? BRAND_PURPLE : 'text.secondary',
                    border: '1px solid rgba(15,23,42,0.12)',
                    bgcolor: tuneMuted ? alpha(BRAND_PURPLE, 0.08) : 'background.paper',
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                  }}
                >
                  {getVolumeIcon()}
                </IconButton>
                <Slider
                  min={0}
                  max={1}
                  step={0.01}
                  value={tuneMuted ? 0 : tuneVolume}
                  onChange={handleVolumeChange}
                  aria-label="Volume"
                  sx={{
                    width: { xs: 56, sm: 80 },
                    flexShrink: 0,
                    color: BRAND_PURPLE,
                    height: 4,
                    '& .MuiSlider-rail': { opacity: 1, color: 'rgba(148,163,184,0.34)', height: 4 },
                    '& .MuiSlider-track': { border: 'none', height: 4 },
                    '& .MuiSlider-thumb': { width: 12, height: 12, bgcolor: '#fff', border: `2px solid ${BRAND_PURPLE}` },
                  }}
                />
                <SoundSettingsButton
                  settings={sound.settings}
                  onChange={sound.update}
                  onReset={sound.reset}
                  supported={sound.supported}
                  sx={{ color: 'text.secondary' }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatPlaybackTime(currentTime)} / {formatPlaybackTime(duration)}
              </Typography>
              <IconButton
                onClick={handleClosePlayer}
                aria-label="Close player"
                size="small"
                sx={{ color: 'text.secondary', border: '1px solid rgba(148,163,184,0.35)', width: 30, height: 30, flexShrink: 0 }}
              >
                <CloseRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
            <Slider
              min={0}
              max={duration || 0}
              step={1}
              value={Math.min(currentTime, duration || 0)}
              onChange={handleSeekChange}
              onChangeCommitted={handleSeekCommit}
              disabled={!duration}
              aria-label="Tune playback progress"
              sx={{
                color: BRAND_PURPLE,
                height: 4,
                '& .MuiSlider-rail': { opacity: 1, color: 'rgba(148,163,184,0.34)', height: 4 },
                '& .MuiSlider-track': { border: 'none', height: 4 },
                '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: '#fff', border: `3px solid ${BRAND_PURPLE}` },
              }}
            />
            <audio
              ref={audioRef}
              crossOrigin="anonymous"
              src={absoluteUrl(current.fileUrl)}
              title={currentTitle}
              preload="metadata"
              style={{ display: 'none' }}
              onLoadedMetadata={() => {
                const audio = audioRef.current;
                setDuration(audio && isFinite(audio.duration) ? audio.duration : 0);
                if (audio && !isSeekingRef.current) {
                  setCurrentTime(audio.currentTime || 0);
                }
              }}
              onDurationChange={() => {
                const audio = audioRef.current;
                setDuration(audio && isFinite(audio.duration) ? audio.duration : 0);
              }}
              onTimeUpdate={() => {
                const audio = audioRef.current;
                if (audio && !isSeekingRef.current) {
                  setCurrentTime(audio.currentTime || 0);
                }
              }}
              onPlay={() => {
                setIsPlaying(true);
                clearPlaybackIssueMessage();
              }}
              onPause={() => {
                setIsPlaying(false);
                if (!intendedPlaybackRef.current) clearPlaybackIssueMessage();
              }}
              onEnded={() => {
                intendedPlaybackRef.current = false;
                isSeekingRef.current = false;
                setIsPlaying(false);
                setCurrentTime(duration || 0);
                clearPlaybackIssueMessage();
              }}
              onSeeking={() => {
                isSeekingRef.current = true;
                clearPlaybackIssueMessage();
              }}
              onSeeked={() => {
                isSeekingRef.current = false;
                clearPlaybackIssueMessage();
                if (intendedPlaybackRef.current) resumePlaybackIfNeeded();
              }}
              onWaiting={() => {
                const audio = audioRef.current;
                if (audio) savedTimeRef.current = audio.currentTime;
                if (intendedPlaybackRef.current) showNetworkIssueMessage();
              }}
              onStalled={() => {
                const audio = audioRef.current;
                if (audio) savedTimeRef.current = audio.currentTime;
                if (intendedPlaybackRef.current) showNetworkIssueMessage();
              }}
              onCanPlay={() => {
                if (intendedPlaybackRef.current) {
                  clearPlaybackIssueMessage();
                  resumePlaybackIfNeeded();
                }
              }}
              onCanPlayThrough={() => {
                if (intendedPlaybackRef.current) {
                  clearPlaybackIssueMessage();
                }
              }}
              onPlaying={() => {
                setIsPlaying(true);
                clearPlaybackIssueMessage();
              }}
              onProgress={() => {
                const audio = audioRef.current;
                if (
                  audio &&
                  intendedPlaybackRef.current &&
                  !audio.paused &&
                  !audio.seeking &&
                  audio.readyState >= 3
                ) {
                  clearPlaybackIssueMessage();
                }
              }}
              onError={() => {
                const audio = audioRef.current;
                if (audio) savedTimeRef.current = audio.currentTime;
                if (intendedPlaybackRef.current) {
                  setPlaybackStatusMessage('Playback paused due to a connection error. Retrying automatically...');
                }
              }}
            >
                Your browser does not support the audio element.
              </audio>
            {playbackStatusMessage && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.68rem',
                  lineHeight: 1.1,
                  color: '#B45309',
                }}
              >
                {playbackStatusMessage}
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
